import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AllActions, SessionState, StoreState } from '../types/types';
import { logger } from '../services/logger.client';

import { createGeneralActions } from './generalActions';
import { createRosterActions } from './rosterActions';
import { createCombatActions } from './combatActions';
import { createPlotActions } from './plotActions';
import { createMapActions } from './mapActions';
import { createLoreActions } from './loreActions';
import { createAuthActions, AuthSlice } from './authActions';
import { createCacheSlice } from './cacheActions';
import { createWorldSlice } from './worldActions';
import { hydratableStateSchema, cacheOnlyHydrationSchema, initialSessionState as emptySession } from './schemas';
import { createInitialState } from './initialState';import { deepMerge } from '../utils/deepMerge';

export const useGlobalState = create<StoreState>()(
  persist(
    immer(
      (set, get, api) => {
        // Define a stable hydrateState function
        const hydrateState: StoreState['hydrateState'] = (newState) => {
            set((store: StoreState) => {
              const keys = Object.keys(newState);

              // Detect cache-only hydration (Phase 2 bootstrap).
              // Cache-only calls have exactly one key: 'cache'.
              // This detection allows us to use a separate schema that doesn't require
              // the full session structure (general, roster, combat, etc.).
              const isCacheOnly = keys.length === 1 && keys[0] === 'cache';

              logger.info(
                { keys, isCacheOnly },
                'hydrateState: Attempting to hydrate state.'
              );

              try {
                if (isCacheOnly) {
                  // =============================================================
                  // CACHE-ONLY HYDRATION PATH (Phase 2 bootstrap)
                  // =============================================================
                  // Uses cacheOnlyHydrationSchema which only validates the cache
                  // payload without requiring session structure. This prevents
                  // false positive validation errors during Phase 2 bootstrap.
                  // =============================================================
                  const result = cacheOnlyHydrationSchema.safeParse(newState);

                  if (!result.success) {
                    // Cache validation failure is non-fatal - log warning and skip update.
                    // We don't reset the session because cache data is supplementary.
                    const detailedError = JSON.stringify(result.error.format(), null, 2);
                    logger.warn(
                      { zodError: detailedError, keys },
                      'Cache-only hydration validation failed. Skipping cache update.'
                    );
                    return;
                  }

                  // Merge validated cache data into existing session.cache
                  logger.info('Cache-only hydration validated successfully. Merging cache data.');
                  const cacheData = result.data.cache;
                  if (cacheData) {
                    // =============================================================
                    // FIX: Track which keys were actually in the original payload
                    // BEFORE Zod added defaults. This prevents Zod's default empty
                    // arrays from overwriting existing cache data.
                    //
                    // Problem: cacheStateSchema.partial() adds .default([]) for all
                    // array fields. When Zod parses { items: [...] }, it adds
                    // spells: [], monsterRoster: [], etc. The old check
                    // `if (cacheData.spells)` was truthy for [], causing overwrites.
                    //
                    // Solution: Only merge fields that were explicitly provided in
                    // the original payload by checking originalCacheKeys.
                    // =============================================================
                    const originalCacheKeys = new Set(
                      Object.keys((newState as { cache?: Record<string, unknown> }).cache ?? {})
                    );

                    // Only merge fields that were explicitly provided in the payload.
                    // This preserves existing cache data when fields are not in the payload.
                    if (originalCacheKeys.has('monsterRoster') && cacheData.monsterRoster !== undefined) {
                      store.session.cache.monsterRoster = cacheData.monsterRoster;
                    }
                    if (originalCacheKeys.has('deities') && cacheData.deities !== undefined) {
                      store.session.cache.deities = cacheData.deities;
                    }
                    if (originalCacheKeys.has('loreCategories') && cacheData.loreCategories !== undefined) {
                      store.session.cache.loreCategories = cacheData.loreCategories;
                    }
                    if (originalCacheKeys.has('imageCategories') && cacheData.imageCategories !== undefined) {
                      store.session.cache.imageCategories = cacheData.imageCategories;
                    }
                    if (originalCacheKeys.has('terrainTypes') && cacheData.terrainTypes !== undefined) {
                      store.session.cache.terrainTypes = cacheData.terrainTypes;
                    }
                    if (originalCacheKeys.has('soundscapeCategories') && cacheData.soundscapeCategories !== undefined) {
                      store.session.cache.soundscapeCategories = cacheData.soundscapeCategories;
                    }
                    if (originalCacheKeys.has('items') && cacheData.items !== undefined) {
                      store.session.cache.items = cacheData.items;
                    }
                    if (originalCacheKeys.has('spells') && cacheData.spells !== undefined) {
                      store.session.cache.spells = cacheData.spells;
                    }
                    if (originalCacheKeys.has('soundscapes') && cacheData.soundscapes !== undefined) {
                      store.session.cache.soundscapes = cacheData.soundscapes;
                    }
                    if (originalCacheKeys.has('treasures') && cacheData.treasures !== undefined) {
                      store.session.cache.treasures = cacheData.treasures;
                    }
                    if (originalCacheKeys.has('weatherConditions') && cacheData.weatherConditions !== undefined) {
                      store.session.cache.weatherConditions = cacheData.weatherConditions;
                    }
                  }
                  return;
                }

                // =============================================================
                // FULL STATE HYDRATION PATH (Critical bootstrap)
                // =============================================================
                // Uses hydratableStateSchema which validates the complete session
                // structure. Validation failure here is a critical error that
                // resets the session to emptySession.
                // =============================================================
                const result = hydratableStateSchema.safeParse(newState);
                if (!result.success) {
                  const detailedError = JSON.stringify(result.error.format(), null, 2);
                  logger.error({ zodError: detailedError }, 'Zod validation failed during state hydration.');
                  logger.warn('hydrateState: Resetting to no-op session due to validation failure.');
                  store.session = emptySession;
                  return;
                }

                const { cache: cacheData, settings: settingsData, ...sessionData } = result.data;

                // If images with gridSettings are present, extract them into the settings.grids record
                if (newState.images && Array.isArray(newState.images)) {
                  const gridsFromImages = (newState.images ?? []).reduce((acc: Record<string, import('../types/types').GridSettings>, img: import('../types/types').Image) => {
                    if (img.gridSettings) {
                      // Convert Prisma's Decimal type to a number for the client-side type.
                      const gridSettings = { ...img.gridSettings, opacity: Number(img.gridSettings.opacity) };
                      acc[img.id] = gridSettings as import('../types/types').GridSettings;
                    }
                    return acc;
                  }, {} as Record<string, import('../types/types').GridSettings>);

                  if (Object.keys(gridsFromImages).length > 0) {
                    store.settings.grids = { ...store.settings.grids, ...gridsFromImages };
                  }
                }

                logger.info({ sessionKeys: Object.keys(sessionData), hasCache: !!cacheData, hasSettings: !!settingsData }, 'hydrateState: Full hydration successful. Setting new session state.');
                if (settingsData) {
                  if (settingsData.grids) {
                    for (const mapId in settingsData.grids) {
                      const grid = settingsData.grids[mapId];
                      grid!.offsetX = grid!.offsetX ?? 0;
                      grid!.offsetY = grid!.offsetY ?? 0;
                    }
                  }
                  store.settings = deepMerge(store.settings, settingsData as StoreState['settings']);
                }

                // Deep merge the session data.
                // We need to be careful with arrays of objects, as deepMerge might try to merge them instead of replacing.
                // By creating a new object that includes the existing cache and the merged session data,
                // we satisfy TypeScript's requirement that the assigned object is a complete SessionState.
                const newSessionData = sessionData as Partial<Omit<SessionState, 'cache'>>;
                const mergedSession = deepMerge({ ...store.session }, newSessionData);
                store.session = { ...mergedSession, cache: store.session.cache };
                if (newSessionData.map?.mapDrawings) {
                  store.session.map.mapDrawings = newSessionData.map.mapDrawings;
                }
                if (cacheData) {
                  // Explicitly merge cache properties to avoid overwriting existing cache slices.
                  // All cache fields from CacheState must be handled here to ensure proper hydration.
                  if (cacheData.monsterRoster) store.session.cache.monsterRoster = cacheData.monsterRoster;
                  if (cacheData.deities) store.session.cache.deities = cacheData.deities;
                  if (cacheData.loreCategories) store.session.cache.loreCategories = cacheData.loreCategories;
                  if (cacheData.imageCategories) store.session.cache.imageCategories = cacheData.imageCategories;
                  if (cacheData.terrainTypes) store.session.cache.terrainTypes = cacheData.terrainTypes;
                  if (cacheData.soundscapeCategories) store.session.cache.soundscapeCategories = cacheData.soundscapeCategories;
                  if (cacheData.items) store.session.cache.items = cacheData.items;
                  if (cacheData.spells) store.session.cache.spells = cacheData.spells;
                  if (cacheData.soundscapes) store.session.cache.soundscapes = cacheData.soundscapes;
                  if (cacheData.treasures) store.session.cache.treasures = cacheData.treasures;
                  if (cacheData.weatherConditions) store.session.cache.weatherConditions = cacheData.weatherConditions;
                }
              } catch (error) {
                logger.error({ err: error, serverState: newState }, 'An unexpected error occurred during state hydration.');
                store.session = emptySession;
              }
            });
          };

        const generalSlice = createGeneralActions(set, get, api);
        const rosterSlice = createRosterActions(set, get, api);
        const combatSlice = createCombatActions(set, get, api);
        const plotSlice = createPlotActions(set, get, api);
        const mapSlice = createMapActions(set, get, api);
        const loreSlice = createLoreActions(set, get, api);
        const authSlice = createAuthActions(set, get, api);
        const cacheSlice = createCacheSlice(set, get, api);
        const worldSlice = createWorldSlice(set, get, api);

        return {
          settings: createInitialState().settings,
          session: {
            general: generalSlice.state,
            roster: rosterSlice.state,
            combat: combatSlice.state,
            plots: plotSlice.state,
            map: mapSlice.state,
            lore: loreSlice.state,
            cache: emptySession.cache,
            world: worldSlice.world,
          },
          auth: { ...authSlice.state, ...authSlice.actions } as AuthSlice,
          cache: cacheSlice.cache,
          actions: { // This needs to be a complete AllActions object
            ...generalSlice.actions, ...rosterSlice.actions, ...combatSlice.actions, ...plotSlice.actions, ...mapSlice.actions,
            ...loreSlice.actions, ...authSlice.actions, ...cacheSlice.cache, ...worldSlice.world,
          } as AllActions,
          hydrateState,
        };
      }
    ),
    {
      name: 'dm-assistant-storage', // name of the item in storage
      storage: createJSONStorage(() => localStorage), // use localStorage
      partialize: (store) => ({
        auth: { token: store.auth.token },
        settings: store.settings,
        // Persist combat state so navigation restores it immediately from localStorage
        // before the server bootstrap completes. Server bootstrap always runs after
        // page load and overwrites with authoritative server state.
        session: { combat: store.session?.combat },
      }),
      merge: (persistedState, currentState) => deepMerge(currentState, persistedState as Partial<StoreState>),
      onRehydrateStorage: () => {
        logger.info('useGlobalState: Hydration from localStorage has started.');
        return (state, error) => {
          if (error) logger.error({ err: error }, 'An error occurred during hydration');
          logger.info('useGlobalState: Hydration from localStorage has finished.');
        }
      },
    }
  )
);